if (!customElements.get('fa-shop-the-look')) {
  customElements.define('fa-shop-the-look', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.resizeObserver?.disconnect();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.positionProduct());
        const scene = this.querySelector('.fa-look__scene');
        if (scene) this.resizeObserver.observe(scene);
      }
      this.addEventListener('toggle', () => this.syncProductPanel(), {capture:true,signal:this.controller.signal});
      window.addEventListener('resize', () => this.positionProduct(), {signal:this.controller.signal});
      this.addEventListener('load', () => this.positionProduct(), {capture:true,signal:this.controller.signal});
      document.addEventListener('click', event => {
        if (!this.overlayActive() || !this.querySelector('.fa-look__product--active')) return;
        if (!this.contains(event.target) || (event.target.closest('.fa-look__scene') && !event.target.closest('[data-fa-look-target]'))) this.closeProduct(false);
      }, {signal:this.controller.signal});
      // Keep the accessible list visible when no scene is configured.
      this.classList.toggle('fa-look--enhanced', Boolean(this.querySelector('.fa-look__scene .fa-look__image')));
      this.addEventListener('click', event => {
        if (event.target.closest('[data-fa-look-close]')) {
          this.closeProduct();
          return;
        }
        const hotspot = event.target.closest('[data-fa-look-target]');
        if (!hotspot) return;
        const target = this.querySelector(`#${CSS.escape(hotspot.dataset.faLookTarget)}`);
        if (!target) return;
        event.preventDefault();
        this.openProduct(target, true);
      }, {signal:this.controller.signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.overlayActive()) {
          event.preventDefault();
          this.closeProduct();
        }
      }, {signal:this.controller.signal});
      document.addEventListener('shopify:block:select', event => {
        const target = event.target.closest('[data-fa-look-block]');
        if (target && this.contains(target)) this.openProduct(target, false);
      }, {signal:this.controller.signal});
    }
    overlayActive() {
      return this.dataset.mode === 'hotspots' && this.dataset.productDisplay === 'overlay'
        && this.classList.contains('fa-look--enhanced') && window.matchMedia('(min-width:750px)').matches;
    }
    openProduct(target, focus) {
      this.querySelectorAll('.fa-look__disclosure').forEach(details => { details.open = target.contains(details); });
      this.querySelectorAll('[data-fa-look-block]').forEach(card => {
        card.classList.toggle('fa-look__product--active', card === target);
      });
      this.querySelectorAll('[data-fa-look-target]').forEach(hotspot => {
        const selected = hotspot.dataset.faLookTarget === target.id;
        hotspot.classList.toggle('fa-look__hotspot--selected', selected);
        if (selected) hotspot.setAttribute('aria-current', 'true');
        else hotspot.removeAttribute('aria-current');
      });
      this.positionProduct();
      if (focus) {
        target.focus({preventScroll:true});
        if (!this.overlayActive()) {
          const products = this.querySelector('.fa-look__products');
          if (products?.hasAttribute('tabindex')) {
            const bounds = products.getBoundingClientRect(), card = target.getBoundingClientRect();
            if (card.top < bounds.top || card.bottom > bounds.bottom) products.scrollTop += card.top - bounds.top;
          } else target.scrollIntoView({block:'nearest',behavior:'auto'});
        }
      }
    }
    syncProductPanel() {
      const image = this.querySelector('.fa-look__image');
      const products = this.querySelector('.fa-look__products');
      if (!products) return;
      const beside = !this.querySelector('.fa-look__layout--above');
      const contain = this.dataset.containProducts === 'true' && this.dataset.mode === 'hotspots'
        && this.dataset.productDisplay === 'accordion' && beside && image
        && window.matchMedia('(min-width:750px)').matches;
      const height = image?.getBoundingClientRect().height || 0;
      if (contain && height > 0) this.style.setProperty('--fa-look-panel-height', `${height}px`);
      else this.style.removeProperty('--fa-look-panel-height');
      if (contain && height > 0 && products.scrollHeight > products.clientHeight + 1) {
        products.tabIndex = 0;
        products.setAttribute('aria-label', 'Shop the look products');
      } else {
        products.removeAttribute('tabindex');
        products.removeAttribute('aria-label');
      }
    }
    positionProduct() {
      this.syncProductPanel();
      if (!this.overlayActive()) return;
      const target = this.querySelector('.fa-look__product--active');
      const trigger = this.querySelector('.fa-look__hotspot--selected');
      const scene = this.querySelector('.fa-look__scene');
      const layout = this.querySelector('.fa-look__layout');
      const products = this.querySelector('.fa-look__products');
      if (!target || !trigger || !scene || !layout || !products) return;
      const bounds = scene.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const origin = layout.getBoundingClientRect();
      const marker = trigger.getBoundingClientRect();
      const inset = 8, gap = 8;
      const width = Math.min(320, Math.max(1, bounds.width - inset * 2));
      products.style.setProperty('--fa-look-popover-width', `${width}px`);
      const below = Math.max(1, bounds.bottom - inset - marker.bottom - gap);
      const above = Math.max(1, marker.top - gap - bounds.top - inset);
      const desiredHeight = Math.max(target.scrollHeight, target.getBoundingClientRect().height);
      const placeAbove = desiredHeight > below && above > below;
      const maxHeight = Math.min(bounds.height - inset * 2, placeAbove ? above : below);
      const height = Math.min(desiredHeight, Math.max(1, maxHeight));
      const left = Math.max(bounds.left + inset, Math.min(marker.left + marker.width / 2 - width / 2, bounds.right - inset - width));
      const preferredTop = placeAbove ? marker.top - gap - height : marker.bottom + gap;
      const top = Math.max(bounds.top + inset, Math.min(preferredTop, bounds.bottom - inset - height));
      products.style.setProperty('--fa-look-popover-left', `${left - origin.left}px`);
      products.style.setProperty('--fa-look-popover-top', `${top - origin.top}px`);
      products.style.setProperty('--fa-look-popover-max-height', `${Math.max(1,maxHeight)}px`);
      products.dataset.placement = placeAbove ? 'above' : 'below';
    }
    closeProduct(restoreFocus = true) {
      const trigger = this.querySelector('.fa-look__hotspot--selected');
      this.querySelectorAll('.fa-look__product--active').forEach(card => card.classList.remove('fa-look__product--active'));
      this.querySelectorAll('.fa-look__hotspot--selected').forEach(hotspot => {
        hotspot.classList.remove('fa-look__hotspot--selected');
        hotspot.removeAttribute('aria-current');
      });
      if (restoreFocus) trigger?.focus({preventScroll:true});
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); }
  });
}
